<template>
   <div>
      <div class="faq-item" v-for="(faq, index) in faqs" :key="index">
         <div class="faq-question" @click="faq.open = !faq.open">
            <i class="fa-solid fa-circle-question faq-icon"></i>
            <span>{{ faq.question }}</span>
            <i class="fa-solid fa-chevron-down faq-chevron" :class="{ 'faq-chevron-open': faq.open }"></i>
         </div>
         <transition name="faq-expand">
            <div v-if="faq.open" class="faq-answer" v-html="faq.answer"></div>
         </transition>
      </div>
   </div>
</template>

<script>
export default {
   data() {
      return {
         faqs: [
            {
               question: "What browsers does XSurfLog support?",
               answer: "XSurfLog is available for <strong>Google Chrome</strong>, <strong>Mozilla Firefox</strong>, and <strong>Microsoft Edge</strong>. All Chromium-based browsers (Brave, Opera, Vivaldi) may also work with the Chrome version, though they are not officially supported.",
               open: false,
            },
            {
               question: "Can I install the extension on ChromeOS / Chromebooks?",
               answer: "Yes. If your organisation manages devices through Google Workspace, you can force-install XSurfLog via the Google Admin Console. See the <strong>Auto-Install Apps & Extensions</strong> section above for step-by-step instructions.",
               open: false,
            },
            {
               question: "The extension isn't logging activity — what should I check?",
               answer: `<ol style="padding-left: 18px;">
                  <li>Confirm the extension is <strong>enabled</strong> in <code>chrome://extensions</code>.</li>
                  <li>Open the extension popup and check if you're <strong>logged in</strong> and the correct organisation is selected.</li>
                  <li>Verify that the domain you're visiting isn't on your <strong>Allow List</strong> (allowed domains are intentionally not logged).</li>
                  <li>Check the <strong>Extension Keys</strong> page — the key must be allocated and not expired.</li>
                  <li>If none of the above work, try uninstalling and reinstalling the extension.</li>
               </ol>`,
               open: false,
            },
            {
               question: "How do extension keys work?",
               answer: "Each XSurfLog extension instance is assigned a unique <strong>extension key</strong> when a user logs in. This key ties the extension to your organisation's account. Administrators can view, manage, and revoke extension keys from the <strong>Extension Keys</strong> page. If a key is revoked or unallocated, the extension will stop logging until a new key is assigned.",
               open: false,
            },
            {
               question: "Can users uninstall the extension themselves?",
               answer: "If deployed via <strong>Group Policy</strong> or <strong>Google Admin Console</strong> with a force-install policy, users <strong>cannot</strong> remove the extension. If installed manually from the browser store, users can remove it at any time.",
               open: false,
            },
            {
               question: "Does the extension slow down browsing?",
               answer: "No. XSurfLog is designed to be lightweight and runs asynchronously in the background. It captures minimal metadata (URL, timestamp, category) and batches uploads to avoid impacting page load performance.",
               open: false,
            },
            {
               question: "How do I update the extension to the latest version?",
               answer: "Extensions installed from the Chrome Web Store, Firefox Add-ons, or Edge Add-ons update <strong>automatically</strong>. Force-installed extensions via Group Policy or Google Admin also receive automatic updates. No manual action is required.",
               open: false,
            },
            {
               question: "Can I deploy XSurfLog to specific users or teams only?",
               answer: "Yes. Both Group Policy (via Organisational Units and Security Groups) and Google Admin Console (via OUs) allow you to target specific user groups. In the XSurfLog portal, you can also manage which extensions belong to which organisations.",
               open: false,
            },
            {
               question: "What data does the extension collect?",
               answer: "XSurfLog records the <strong>URL visited</strong>, <strong>timestamp</strong>, <strong>page title</strong>, and the <strong>website category</strong> (e.g. Social Media, News, Productivity). It does <strong>not</strong> capture form inputs, passwords, or page content. All data is transmitted over HTTPS and stored securely.",
               open: false,
            },
         ]
      }
   }
}
</script>

<style scoped>
.faq-item {
   border: 1px solid #e9ecef;
   border-radius: 10px;
   margin-bottom: 8px;
   overflow: hidden;
}

.faq-question {
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 14px 18px;
   cursor: pointer;
   font-weight: 600;
   font-size: 14px;
   color: #435666;
   transition: background 0.15s ease;
}
.faq-question:hover { background: #f9fafb; }

.faq-icon { color: #00A0C1; font-size: 16px; flex-shrink: 0; }

.faq-chevron {
   margin-left: auto;
   font-size: 12px;
   color: #adb5bd;
   transition: transform 0.2s ease;
   flex-shrink: 0;
}
.faq-chevron-open { transform: rotate(180deg); }

.faq-answer {
   padding: 0 18px 16px 44px;
   font-size: 13px;
   color: #6c757d;
   line-height: 1.7;
}

/* Transition */
.faq-expand-enter-active,
.faq-expand-leave-active {
   transition: all 0.2s ease;
   overflow: hidden;
}
.faq-expand-enter-from,
.faq-expand-leave-to {
   opacity: 0;
   max-height: 0;
   padding-top: 0;
   padding-bottom: 0;
}
</style>
